import React, { useState, useCallback, useRef, useEffect } from 'react'
import Wrapper from '../../components/Wrapper'
import { openAi, shortFunction, input } from '../../translate'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Typography, IconButton, Paper, TextField, MenuItem, Button, Alert, InputAdornment, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import Api from '../../Api'
import { REACT_APP_API_URL } from '../../config'
import SearchIcon from '@mui/icons-material/Search'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import AudioPlayerBar from '../../components/AudioPlayerBar'

const OPENAI_GENERATED_STORAGE_KEY = 'openai_generated_files'
const { REACT_APP_SERVER } = process.env
export default function OpenAI() {
    const langIndex = +localStorage.getItem('atsLang') || 1;
    const navigate = useNavigate();
    const location = useLocation();
    const fromState = location.state;

    const [form, setForm] = useState({ title: '', description: '', voice: '' });
    const [voices] = useState(['alloy', 'nova', 'fable', 'shimmer'])
    const [voicesWav] = useState(['Luse', 'Areg', 'Avet', 'Tigran', 'Ani',])
    const [processing, setProcessing] = useState(false);
    const [resultFile, setResultFile] = useState(null);
    const [generatedFiles, setGeneratedFiles] = useState([]);
    const audioRef = useRef(null);
    const [playingId, setPlayingId] = useState(null);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [volume, setVolume] = useState(1);
    const [speed, setSpeed] = useState(1);
    const speedRef = useRef(speed);
    useEffect(() => {
        speedRef.current = speed;
    }, [speed]);
    const [editingFid, setEditingFid] = useState(null);
    const [editingName, setEditingName] = useState('');

    const addToGeneratedFiles = useCallback((file) => {
        if (!file) return
        const fid = file?.fname || file?.id || file?.name
        if (!fid) return
        setGeneratedFiles((prev) => {
            const list = Array.isArray(prev) ? prev : []
            const exists = list.some((f) => (f?.fname || f?.id || f?.name) === fid)
            if (exists) return list
            return [{ ...file, _addedAt: Date.now() }, ...list]
        })
    }, [])

    useEffect(() => {
        const list = Array.isArray(generatedFiles) ? generatedFiles : []
        if (list.length > 0) {
            try {
                sessionStorage.setItem(OPENAI_GENERATED_STORAGE_KEY, JSON.stringify(list))
            } catch (e) {
                console.warn('Could not persist generated files', e)
            }
        }
    }, [generatedFiles])

    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        const onLoadedMetadata = () => {
            el.playbackRate = speedRef.current;
            setDuration(el.duration);
        };
        const onTimeUpdate = () => setCurrentTime(el.currentTime);
        const onEnded = () => {
            setPlayingId(null);
            setCurrentTime(0);
        };
        el.addEventListener('loadedmetadata', onLoadedMetadata);
        el.addEventListener('timeupdate', onTimeUpdate);
        el.addEventListener('ended', onEnded);
        return () => {
            el.removeEventListener('loadedmetadata', onLoadedMetadata);
            el.removeEventListener('timeupdate', onTimeUpdate);
            el.removeEventListener('ended', onEnded);
        };
    }, []);

    useEffect(() => {
        const el = audioRef.current;
        if (el) el.volume = volume;
    }, [volume]);

    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        el.playbackRate = speed;
    }, [speed, currentTrack?.fname, currentTrack?.id, currentTrack?.name, playingId]);

    const keySuffix = '&key=akNwNXN1U2k4bzdEQ09QU29KMUpYbXFiQTV5c3FmUm1MOUNheW5qWlYwOD0=';
    const getAudioSrc = (f) => {
        const fid = f?.fname || f?.id || f?.name;
        return fid ? `${REACT_APP_API_URL}/rest/files/audio?file=${fid}${keySuffix}` : null;
    };
    const handleChange = useCallback((name, ev) => {
        setForm((prevState) => ({
            ...prevState,
            [name]: ev
        }))
        if (name === "voice") {
            const audio = new Audio(`${REACT_APP_API_URL}${((REACT_APP_SERVER === 'Ucom' || REACT_APP_SERVER === 'V-office') ? '/rest' : '')}/file_voice/openai/${ev}_sample.mp3`);
            audio.play().then(r => console.log('Audio playing')).catch(e => console.error('Error playing audio:', e));
        }

    }, [])
    const stopAudio = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setPlayingId(null);
        setCurrentTime(0);
    }, []);

    const handlePlay = useCallback((file) => {
        const fid = file?.fname || file?.id || file?.name;
        const src = getAudioSrc(file);
        if (!fid || !src) return;
        const el = audioRef.current;
        if (!el) return;
        if (playingId === fid) {
            el.pause();
            setPlayingId(null);
            return;
        }
        try {
            el.src = src;
            el.load();
            el.playbackRate = speedRef.current;
            setCurrentTrack(file);
            setCurrentTime(0);
            setDuration(0);
            setPlayingId(fid);
            el.play().catch((e) => {
                console.error('Play failed:', e);
                stopAudio();
            });
        } catch (e) {
            console.log(e);
            stopAudio();
        }
    }, [playingId, stopAudio]);

    const handlePlayerPlayPause = useCallback(() => {
        const el = audioRef.current;
        if (!el || !currentTrack) return;
        if (playingId) {
            el.pause();
            setPlayingId(null);
        } else {
            if (!el.src) {
                const src = getAudioSrc(currentTrack);
                if (src) {
                    el.src = src;
                    el.load();
                    el.playbackRate = speedRef.current;
                    setPlayingId(currentTrack?.fname || currentTrack?.id || currentTrack?.name);
                    el.play().catch((e) => {
                        console.error('Play failed:', e);
                        setPlayingId(null);
                    });
                }
            } else {
                el.playbackRate = speedRef.current;
                setPlayingId(currentTrack?.fname || currentTrack?.id || currentTrack?.name);
                el.play().catch((e) => {
                    console.error('Play failed:', e);
                    setPlayingId(null);
                });
            }
        }
    }, [currentTrack, playingId]);

    const handleSeek = useCallback((_, value) => {
        const el = audioRef.current;
        if (!el || !duration) return;
        const t = (Number(value) / 100) * duration;
        el.currentTime = t;
        setCurrentTime(t);
    }, [duration]);

    const formatTime = (s) => {
        if (!s || !Number.isFinite(s)) return '0:00';
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec < 10 ? '0' : ''}${sec}`;
    };

    const formatFileSize = (bytes) => {
        if (bytes == null || bytes === 0) return '—';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const formatFileDate = (value) => {
        if (!value) return '—';
        const d = value instanceof Date ? value : new Date(typeof value === 'number' ? value : value);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString();
    };

    const handleDownload = async (file) => {
        const fid = file?.fname || file?.id || file?.name;
        if (!fid) return;
        try {
            const url = `${REACT_APP_API_URL}/rest/files/audio?file=${fid}${keySuffix}`;
            const res = await fetch(url);
            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `${file.name || file.fname || fid}.mp3`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (e) {
            console.log(e);
        }
    };

    const handleDelete = async (file) => {
        const fileId = file?.id;
        if (fileId == null) return;
        try {
            await Api.fileDelete(fileId);
            const fid = file?.fname || file?.id || file?.name;
            if (playingId === fid) stopAudio();
            if (currentTrack?.id === fileId) setCurrentTrack(null);
            setGeneratedFiles((prev) => prev.filter((f) => f?.id !== fileId));
            toast.success(langIndex === 2 ? 'Файл удалён' : langIndex === 3 ? 'Ֆայլը ջնջվել է' : 'File deleted');
        } catch (e) {
            console.log(e);
            toast.error(langIndex === 2 ? 'Не удалось удалить файл' : langIndex === 3 ? 'Չհաջողվեց ջնջել' : 'Failed to delete file');
        }
    };

    const handleEditClick = useCallback((file) => {
        const fid = file?.fname || file?.id || file?.name;
        if (!fid) return;
        setEditingFid(fid);
        setEditingName((file?.name || file?.fname || fid || '').toString().trim());
    }, []);

    const handleSaveEdit = useCallback(async () => {
        if (editingFid == null || editingName.trim() === '') return;
        try {
            await Api.fileChangeName(editingFid, editingName.trim());
            setGeneratedFiles((prev) =>
                prev.map((f) => {
                    const fid = f?.fname || f?.id || f?.name;
                    if (fid === editingFid) return { ...f, name: editingName.trim() };
                    return f;
                })
            );
            if (currentTrack && (currentTrack?.fname || currentTrack?.id || currentTrack?.name) === editingFid) {
                setCurrentTrack((t) => (t ? { ...t, name: editingName.trim() } : null));
            }
            setEditingFid(null);
            setEditingName('');
            toast.success(langIndex === 2 ? 'Название сохранено' : langIndex === 3 ? 'Անունը պահպանվել է' : 'Title saved');
        } catch (e) {
            toast.error(langIndex === 2 ? 'Не удалось сохранить' : langIndex === 3 ? 'Չհաջողվեց պահպանել' : 'Failed to save');
        }
    }, [editingFid, editingName, currentTrack, langIndex]);

    const handleCancelEdit = useCallback(() => {
        setEditingFid(null);
        setEditingName('');
    }, []);

    useEffect(() => {
        const fetchGeneratedFiles = async () => {
            const { data } = await Api.getGeneratedFiles(fromState?.from === 'openai-card'?'openai':'wavam');
            setGeneratedFiles(data.files);
        }
        fetchGeneratedFiles();
    }, [fromState?.from])

    const handleAdd = useCallback(async () => {
        const list = Array.isArray(generatedFiles) ? generatedFiles : [];
        const titleTrim = (form.title || '').trim().toLowerCase();
        const nameExists = list.some((f) => String(f?.name || f?.fname || '').toLowerCase() === titleTrim);
        if (nameExists) {
            toast.error(langIndex === 2 ? 'Файл с таким названием уже существует' : langIndex === 3 ? 'Այդ անունով ֆայլն արդեն կա' : 'A file with this name already exists');
            return;
        }
        try {
            setProcessing(true);
            if (fromState?.from === 'openai-card') {
                const { data } = await Api.fileOpenAi({voiceName:form.voice,name:form.title,text:form.description});
                if (data.status === 'processing') {
                    toast.info("Processing file, please wait...");
                    const fileId = data.fileId;
                    const poll = async () => {
                        try {
                            const res = await Api.fetchOpenAi(`${fileId}`);
                            if (res.data.status === 'ready') {
                                toast.success("File is ready");
                                const file = res.data.file || { id: fileId };
                                setResultFile(file);
                                addToGeneratedFiles(file);
                                setProcessing(false);
                                const { data: listData } = await Api.getGeneratedFiles(fromState?.from === 'openai-card' ? 'openai' : 'wavam');
                                if (listData?.files) setGeneratedFiles(listData.files);
                            } else if (res.data.status === 'failed') {
                                toast.error("Failed to process the file");
                                setProcessing(false);
                            } else {
                                setTimeout(poll, 3000);
                            }
                        } catch (e) {
                            console.log(e);
                            setProcessing(false);
                        }
                    };
                    poll();
                } else if (data.file) {
                    setResultFile(data.file);
                    addToGeneratedFiles(data.file);
                    setProcessing(false);
                    const { data: listData } = await Api.getGeneratedFiles('openai');
                    if (listData?.files) setGeneratedFiles(listData.files);
                } else {
                    setProcessing(false);
                }
            } else {
                const { data } = await Api.fileWav({voiceName:form.voice,name:form.title,text:form.description});
                if (data.status === 'processing') {
                    toast.info("Processing file, please wait...");
                }
                if (data.file) {
                    const file = data.file[data.file?.length - 1] || { id: data.file[0].id };
                    setResultFile(file);
                    addToGeneratedFiles(file);
                    const { data: listData } = await Api.getGeneratedFiles('wavam');
                    if (listData?.files) setGeneratedFiles(listData.files);
                }
                setProcessing(false);
            }
        } catch (error) {
            console.log(error);
            setProcessing(false);
        }
    }, [form, fromState, addToGeneratedFiles, generatedFiles, langIndex])

    const fileList = Array.isArray(generatedFiles) ? generatedFiles : [];
    const filteredFileList = searchQuery.trim()
        ? fileList.filter((f) => {
            const label = (f?.name || f?.fname || f?.id || '').toString().toLowerCase();
            return label.includes(searchQuery.trim().toLowerCase());
          })
        : fileList;
    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
    const playerLabel = currentTrack ? (currentTrack?.name || currentTrack?.fname || currentTrack?.id || currentTrack?.name) : (langIndex === 2 ? 'Нет трека' : langIndex === 3 ? 'Երգ չկա' : 'No track');

    const currentTrackFid = currentTrack ? (currentTrack?.fname || currentTrack?.id || currentTrack?.name) : null;
    const currentIndex = currentTrackFid != null ? filteredFileList.findIndex((f) => (f?.fname || f?.id || f?.name) === currentTrackFid) : -1;
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex >= 0 && currentIndex < filteredFileList.length - 1;

    const handlePrev = useCallback(() => {
        if (!canGoPrev) return;
        const prevFile = filteredFileList[currentIndex - 1];
        if (prevFile) handlePlay(prevFile);
    }, [canGoPrev, currentIndex, filteredFileList, handlePlay]);

    const handleNext = useCallback(() => {
        if (!canGoNext) return;
        const nextFile = filteredFileList[currentIndex + 1];
        if (nextFile) handlePlay(nextFile);
    }, [canGoNext, currentIndex, filteredFileList, handlePlay]);

    return (
        <Wrapper>
            <audio ref={audioRef} style={{ display: 'none' }} />
            <div className="tableParent">
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            sx={{ color: '#7ebc0a', fontSize: 16 }}
                            onClick={() => navigate(-1)}
                            aria-label="Back"
                        >
                            <ArrowBackIosNewIcon sx={{ fontSize: 18, fontWeight: 700 }} />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
                            {openAi.openai[langIndex]}
                        </Typography>
                    </Box>

                    <Alert icon={<InfoIcon />} severity="success" sx={{ mt: 2, mb: 2 }}>
                        <Typography variant="body2">
                            {openAi.openai_desc?.[langIndex] ||
                                'Use AI tools like OpenAI APIs to generate and manage voice content for your telephony workflows.'}
                        </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                        <TextField
                            label={openAi.openai_tit?.[langIndex] || 'Title'}
                            value={form.title}
                            onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                            fullWidth
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#7ebc0a' },
                                    '&:hover fieldset': { borderColor: '#6aa507' },
                                    '&.Mui-focused fieldset': { borderColor: '#7ebc0a' },
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#7ebc0a' },
                            }}
                        />
                        <TextField
                            label={openAi.openai_des?.[langIndex] || 'Description'}
                            value={form.description}
                            onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#7ebc0a' },
                                    '&:hover fieldset': { borderColor: '#6aa507' },
                                    '&.Mui-focused fieldset': { borderColor: '#7ebc0a' },
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#7ebc0a' },
                            }}
                        />
                        <TextField
                            select
                            label={openAi.voice_select?.[langIndex] || 'Voice'}
                            value={form.voice}
                            onChange={(e) => handleChange('voice', e.target.value)}
                            fullWidth
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#7ebc0a' },
                                    '&:hover fieldset': { borderColor: '#6aa507' },
                                    '&.Mui-focused fieldset': { borderColor: '#7ebc0a' },
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#7ebc0a' },
                            }}
                        >
                            {fromState.from === 'openai-card' ? voices.map(v => (
                                <MenuItem key={v} value={v}>{v}</MenuItem>
                            )):voicesWav.map(v => (
                                <MenuItem key={v} value={v}>{v}</MenuItem>
                            ))}
                        </TextField>

                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                sx={{ textTransform: 'none', color: '#7ebc0a', borderColor: '#7ebc0a', '&:hover': { borderColor: '#6aa507', color: '#6aa507' } }}
                                onClick={() => navigate(-1)}
                            >
                                {shortFunction.operator_close?.[langIndex] || 'Cancel'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleAdd}
                                sx={{ textTransform: 'none', backgroundColor: '#7ebc0a', '&:hover': { backgroundColor: '#6aa507' } }}
                                disabled={processing || !form.title || !form.description || !form.voice}
                            >
                                {shortFunction.operator_save?.[langIndex] || 'Save'}
                            </Button>
                        </Box>

                        
                    </Box>
                </Paper>
            </div>

            <AudioPlayerBar
                onPrev={handlePrev}
                onNext={handleNext}
                onPlayPause={handlePlayerPlayPause}
                onSeek={handleSeek}
                onDownload={handleDownload}
                currentTrack={currentTrack}
                playingId={playingId}
                progress={progress}
                duration={duration}
                currentTime={currentTime}
                volume={volume}
                onVolumeChange={setVolume}
                playerLabel={playerLabel}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                formatTime={formatTime}
                formatFileSize={formatFileSize}
                formatFileDate={formatFileDate}
                langIndex={langIndex}
                speed={speed}
                onSpeedChange={setSpeed}
            />

            <div className="tableParent" style={{ marginTop: '10px' }}>
                <Paper sx={{ p: 3, pb: 5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, pb: 1.5, borderBottom: '1px solid #e5e8ed' }}>
                        {openAi.generated_files?.[langIndex] || 'Generated files'}
                    </Typography>
                    {fileList.length > 0 && (
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={input.search?.[langIndex] || 'Search'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#7ebc0a' },
                                    '&:hover fieldset': { borderColor: '#6aa507' },
                                    '&.Mui-focused fieldset': { borderColor: '#7ebc0a' },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#7ebc0a', fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}
                    {filteredFileList.length > 0 ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                                gap: 1.5,
                                mt: 1,
                            }}
                        >
                            {filteredFileList.map((file) => {
                                const fid = file?.fname || file?.id || file?.name
                                const label = file?.name || file?.fname || fid
                                return (
                                    <Paper
                                        key={file._addedAt ?? fid}
                                        elevation={0}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            px: 2,
                                            py: 1.5,
                                            border: '1px solid #e5e8ed',
                                            borderRadius: 2,
                                            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
                                            transition: 'box-shadow 0.35s, border-color 0.35s',
                                            '&:hover': {
                                                borderColor: '#7ebc0a',
                                                boxShadow: '0 6px 20px rgba(126, 188, 10, 0.35)',
                                            },
                                        }}
                                    >
                                        {editingFid === fid ? (
                                            <>
                                                <TextField
                                                    size="small"
                                                    value={editingName}
                                                    onChange={(e) => setEditingName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleSaveEdit();
                                                        if (e.key === 'Escape') handleCancelEdit();
                                                    }}
                                                    autoFocus
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        '& .MuiOutlinedInput-root': {
                                                            fontSize: '0.875rem',
                                                            '& fieldset': { borderColor: '#7ebc0a' },
                                                            '&.Mui-focused fieldset': { borderColor: '#7ebc0a' },
                                                        },
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    sx={{ color: '#7ebc0a', minWidth: 32, p: 0.5, textTransform: 'none' }}
                                                    onClick={handleSaveEdit}
                                                >
                                                    {shortFunction.operator_save?.[langIndex] || 'Save'}
                                                </Button>
                                                <Button
                                                    size="small"
                                                    sx={{ color: 'text.secondary', minWidth: 32, p: 0.5, textTransform: 'none' }}
                                                    onClick={handleCancelEdit}
                                                >
                                                    {shortFunction.operator_close?.[langIndex] || 'Cancel'}
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    sx={{ minWidth: 40, color: '#7ebc0a', p: 0.5, fontSize: '1.05rem', '&:hover': { backgroundColor: 'rgba(126, 188, 10, 0.08)' } }}
                                                    onClick={() => handlePlay(file)}
                                                >
                                                    {playingId === fid ? '■' : '▶'}
                                                </Button>
                                                <Tooltip title={langIndex === 2 ? 'Редактировать' : langIndex === 3 ? 'Խմբագրել' : 'Edit'}>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#1976d2', p: 0.5, '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } }}
                                                        onClick={() => handleEditClick(file)}
                                                        aria-label="Edit"
                                                    >
                                                        <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#d32f2f', p: 0.5, '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' } }}
                                                    onClick={() => handleDelete(file)}
                                                    aria-label="Delete"
                                                >
                                                    <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 18 }} />
                                                </IconButton>
                                            </>
                                        )}
                                    </Paper>
                                )
                            })}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {fileList.length > 0
                                ? (langIndex === 2 ? 'Ничего не найдено' : langIndex === 3 ? 'Ոչինչ չի գտնվել' : 'No files match your search.')
                                : (openAi.no_generated_files?.[langIndex] || 'No files generated yet.')}
                        </Typography>
                    )}
                </Paper>
            </div>

        </Wrapper>
    )
}
